import type { UserRoleResponseDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { useUpdateUserRole } from "Settings/User/Api/Mutation/updateUserRoleMutation";
import { getUserRolesEditFormModelSchema } from "Settings/User/Components/Form/Schema/userRolesEditFormSchema";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import { Scrollbar } from "Shared/Components/Scroll/Scrollbar";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Stack } from "@mui/material";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import PermissionsSection from "./PermissionsSection";

type Props = {
	data: UserRoleResponseDto;
};

const userResource = Resources.base.user.roles;

export const UserRolesEditForm: React.FC<Props> = ({ data }) => {
	const { t } = useResource();
	const form = useForm({
		resolver: zodResolver(getUserRolesEditFormModelSchema()),
		defaultValues: {
			...data,
			permissions: data?.permissions || [],
		},
	});

	const {
		control,
		reset,
		formState: { errors },
	} = form;

	useEffect(() => {
		reset({
			...data,
			permissions: data?.permissions || [],
		});
	}, [data]);

	const { mutate: updateUserRole, isPending } = useUpdateUserRole(data.name);
	const { handleServerError } = useHandleServerError(form);
	const handleSubmitRolesUpdate = () => {
		handleServerError((data) => updateUserRole(data));
	};

	return (
		<form>
			<Stack spacing={2}>
				<Input
					id="name"
					name="name"
					label={t("Role Name")}
					variant="outlined"
					control={control}
					errors={errors}
					disabled
					fullWidth
				/>
				<Scrollbar sx={{ maxHeight: 400 }}>
					<PermissionsSection form={form} />
				</Scrollbar>

				<Button
					fullWidth
					variant="contained"
					onClick={handleSubmitRolesUpdate}
					disabled={isPending}
				>
					{t(userResource.save)}
				</Button>
			</Stack>
		</form>
	);
};
